/**
 * useGlobalUppercase — trasforma automaticamente tutto ciò che l'utente digita
 * in caratteri MAIUSCOLI, in modo trasparente per ogni pagina/componente.
 *
 * Come funziona:
 *  1. Aggiunge un listener globale a `document` sull'evento `input`.
 *  2. Se il target è un <input> di tipo testuale o un <textarea> non escluso,
 *     converte `event.target.value` in maiuscolo e invia un `Event('input')`
 *     nativo affinché React aggiorni il proprio state controllato.
 *
 * Esclusioni automatiche:
 *  - password, email, url, tel, number, date, datetime-local, time, file, hidden, color, search-native
 *  - qualsiasi elemento con attributo `data-no-uppercase` (o antenato con esso)
 *  - qualsiasi elemento con `autocapitalize="off"` (comportamento coerente col
 *    web standard: se lo sviluppatore ha detto "non trasformare", rispettiamo)
 *
 * Aspetto visivo: gestito da CSS globale in App.css (text-transform: uppercase).
 */
import { useEffect } from "react";

const EXCLUDED_INPUT_TYPES = new Set([
  "password",
  "email",
  "url",
  "tel",
  "number",
  "date",
  "datetime-local",
  "month",
  "time",
  "week",
  "file",
  "hidden",
  "color",
  "range",
  "checkbox",
  "radio",
  "submit",
  "reset",
  "button",
  "image",
]);

function shouldUppercase(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag !== "INPUT" && tag !== "TEXTAREA") return false;
  // opt-out esplicito
  if (el.closest && el.closest("[data-no-uppercase]")) return false;
  if (el.getAttribute && el.getAttribute("autocapitalize") === "off") return false;
  if (tag === "INPUT") {
    const type = (el.type || "text").toLowerCase();
    if (EXCLUDED_INPUT_TYPES.has(type)) return false;
  }
  return true;
}

// Trigger nativo che notifica React del cambio valore (bypass del setter proxy)
function setNativeValue(el, value) {
  const proto = el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
  if (descriptor && descriptor.set) {
    descriptor.set.call(el, value);
  } else {
    el.value = value;
  }
}

export default function useGlobalUppercase() {
  useEffect(() => {
    let composing = false; // IME (giapponese/cinese/dead-key accenti) — non toccare
    const onCompStart = () => { composing = true; };
    const onCompEnd = () => { composing = false; };

    const onInput = (ev) => {
      if (composing) return;
      const el = ev.target;
      if (!shouldUppercase(el)) return;
      const original = el.value;
      if (!original) return;
      const upper = original.toLocaleUpperCase("it-IT");
      if (upper === original) return;
      // preserva la posizione del cursore
      const start = el.selectionStart;
      const end = el.selectionEnd;
      setNativeValue(el, upper);
      try {
        if (typeof start === "number") el.setSelectionRange(start, end);
      } catch { /* alcune input (date/etc) non supportano selection */ }
      // Notifica React (dispatchEvent bubbles=true attiva l'onChange controllato)
      el.dispatchEvent(new Event("input", { bubbles: true }));
    };

    document.addEventListener("input", onInput, true);
    document.addEventListener("compositionstart", onCompStart, true);
    document.addEventListener("compositionend", onCompEnd, true);
    return () => {
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("compositionstart", onCompStart, true);
      document.removeEventListener("compositionend", onCompEnd, true);
    };
  }, []);
}
